import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Grid3x3, Zap, ChevronLeft, ChevronRight } from 'lucide-react';
import { useStudioStore } from '@/store/useStudioStore';

export function PatternSequencer() {
  const channels = useStudioStore((state) => state.channels);
  const selectedChannelId = useStudioStore((state) => state.selectedChannelId);
  const currentStep = useStudioStore((state) => state.currentStep);
  const toggleStep = useStudioStore((state) => state.toggleStep);
  
  const steps = 16;
  const [zoom, setZoom] = useState(1);
  const [snapValue] = useState('1/16');
  const [showScrollIndicators, setShowScrollIndicators] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Check if scrolling is needed and update scroll indicators
  useEffect(() => {
    const checkScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
        const hasScroll = scrollWidth > clientWidth;
        setShowScrollIndicators(hasScroll);
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
      }
    };

    checkScroll();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
      return () => {
        container.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [zoom, channels]);

  // Scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  return (
    <div className="flex-1 bg-[#2a2a2a] border-t border-[#3a3a3a] overflow-auto flex flex-col">
      <div className="h-8 bg-[#1f1f1f] flex items-center px-3 border-b border-[#3a3a3a] justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xs">STEP SEQUENCER</span>
          <div className="flex items-center gap-2">
            <button className="text-[10px] px-2 py-0.5 bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded flex items-center gap-1">
              <Grid3x3 size={10} />
              Snap: {snapValue}
              <ChevronDown size={10} />
            </button>
            <button className="text-[10px] px-2 py-0.5 bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded flex items-center gap-1">
              <Zap size={10} />
              Swing
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] opacity-50">Zoom</span>
          <input
            type="range"
            min="0.5"
            max="2"
            step="0.1"
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-16"
          />
        </div>
      </div>
      
      <div className="flex-1 overflow-auto p-4 relative" ref={scrollContainerRef}>
        {/* Scroll indicators */}
        {showScrollIndicators && canScrollLeft && (
          <button
            onClick={scrollLeft}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-[#ff6b00] hover:bg-[#ff8b30] text-white rounded-full p-2 shadow-lg transition-all"
            title="Scroll left"
          >
            <ChevronLeft size={20} />
          </button>
        )}
        {showScrollIndicators && canScrollRight && (
          <button
            onClick={scrollRight}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-[#ff6b00] hover:bg-[#ff8b30] text-white rounded-full p-2 shadow-lg transition-all"
            title="Scroll right"
          >
            <ChevronRight size={20} />
          </button>
        )}
        
        <div className="inline-block" style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
          {/* Time ruler */}
          <div className="flex mb-2 ml-24">
            {Array.from({ length: steps }, (_, i) => (
              <div
                key={i}
                className="flex flex-col items-center"
                style={{ width: '40px' }}
              >
                <div className="text-[10px] opacity-50 mb-1">{i + 1}</div>
                <div className={`w-px ${i % 4 === 0 ? 'h-3 bg-[#ff6b00]' : 'h-2 bg-[#4a4a4a]'}`} />
              </div>
            ))}
          </div>
          
          {/* Grid */}
          {channels.map((channel) => (
            <div key={channel.id} className="flex items-center gap-2 mb-1.5">
              <div className={`w-20 text-xs px-2 py-1 rounded truncate ${
                channel.id === selectedChannelId 
                  ? 'bg-[#ff6b00] text-white' 
                  : 'bg-[#3a3a3a] opacity-70'
              }`}>
                {channel.name}
              </div>
              <div className="flex gap-1">
                {channel.steps.map((isActive, stepIndex) => (
                  <div key={stepIndex} className="flex flex-col items-center gap-0.5">
                    <button
                      onClick={() => toggleStep(channel.id, stepIndex)}
                      className={`w-10 h-10 rounded relative ${
                        isActive
                          ? 'bg-[#ff6b00] shadow-lg shadow-[#ff6b00]/30'
                          : stepIndex % 4 === 0
                          ? 'bg-[#3a3a3a]'
                          : 'bg-[#2a2a2a]'
                      } ${
                        currentStep === stepIndex ? 'ring-2 ring-white' : ''
                      } hover:bg-[#ff8b30] transition-all border border-[#1a1a1a] group`}
                      title={isActive ? 'Active' : 'Click to activate'}
                    >
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Bottom info bar */}
      <div className="h-6 bg-[#1f1f1f] border-t border-[#3a3a3a] px-3 flex items-center justify-between text-[10px] opacity-70">
        <span>Click to add/remove notes • Scroll wheel on active steps to adjust velocity</span>
        <span>Pattern length: {steps} steps</span>
      </div>
    </div>
  );
}